import './index.css'
import captureError from '@disco/utils/captureError'
import * as React from 'react'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { PasswordStrength } from 'types/business'
import {
  checkPasswordForBreaches,
  checkPasswordSecurity,
} from '@disco/utils/password'
import PasswordRequirementsList from 'components/password/password-requirements-list'
import WeakPasswordError from 'components/password/weak-password-error'
import LoginError from 'components/utils/login-error'
import { BREACH_ERROR } from './constants'
import ChangePasswordInvitesForm from './invites-form'
import ChangePasswordLoginForm from './login-form'
import ChangePasswordSettingsForm from './settings-form'
import ChangePasswordWelcomeForm from './welcome-form'
import type { HttpErrorType } from 'types/errors'
type Props = {
  type: 'settings' | 'login' | 'welcome' | 'invite'
  errorCode?: string
  passwordChangeError?: HttpErrorType | Error | null | undefined
  passwordChangeInProgress?: boolean
  validate?: (...args: Array<any>) => any
  getValidationMessages?: (...args: Array<any>) => any
  onPasswordChange: (...args: Array<any>) => any
  // TODO: It is required to change the invite page to remove the onInput and validationOnInput properties
  onInput?: (...args: Array<any>) => any
  validationOnInput?: boolean
  passwordStrength: PasswordStrength
}

/**
 * Password change component with validation, strength checking, and breach detection
 * Supports multiple contexts: settings, login, welcome, and invite flows
 * Includes real-time validation and security requirements enforcement
 */
class ChangePassword extends React.Component<
  Props,
  {
    oldPassword: string
    newPassword: string
    passwordConfirmation: string
    securityRequirementsError: React.ReactNode | null | undefined
    newPasswordError: React.ReactNode | null | undefined
    oldPasswordError: React.ReactNode | null | undefined
    passwordConfirmationError: React.ReactNode | null | undefined
    serverError: React.ReactNode
  }
> {
  // read by react-validation-mixin
  validatorTypes: any

  constructor(props: Props) {
    super(props)
    this.state = {
      oldPassword: '',
      newPassword: '',
      passwordConfirmation: '',
      securityRequirementsError: null,
      newPasswordError: null,
      oldPasswordError: null,
      passwordConfirmationError: null,
      serverError: null,
    }
    this.validatorTypes = strategy.createSchema({
      password: 'confirmed',
    })
  }

  getValidatorData = () => {
    return {
      password: this.state.newPassword,
      password_confirmation: this.state.passwordConfirmation,
    }
  }
  _resetErrors = () => {
    if (this.props.validationOnInput) {
      this._checkErrors('new')

      this._checkErrors('confirm')

      this._checkErrors('server')
    } else {
      this.setState({
        securityRequirementsError: null,
        newPasswordError: null,
        oldPasswordError: null,
        passwordConfirmationError: null,
        serverError: null,
      })
    }
  }
  _changePassword = () => {
    this._checkErrors('new')

    this._checkErrors('confirm')

    let passwordSecure = checkPasswordSecurity(
      this.state.newPassword,
      this.props.passwordStrength,
    )
    this.props.validate(async error => {
      if (error || !passwordSecure) {
        return
      }

      try {
        const numBreached = await checkPasswordForBreaches(
          this.state.newPassword,
        )

        if (numBreached) {
          const newPasswordError = BREACH_ERROR
          this.setState({
            newPasswordError,
          })
          return
        }
      } catch (err) {
        console.error('checkPasswordForBreaches error', err) // ignore failures

        captureError(err)
      }

      this.props.onPasswordChange(
        this.state.newPassword,
        this.state.oldPassword,
      )
    })
  }
  _getSecurityRequirementsErrorCaption = error => {
    if (['extra_strong', 'strong'].includes(this.props.passwordStrength)) {
      return 'Please choose a stronger password'
    }

    return error || this.state.securityRequirementsError
  }
  _checkErrors = (type: 'new' | 'confirm' | 'server') => {
    const validateCb = () => {
      const validationMessages = this.props
        .getValidationMessages('password')
        .toString()
      let error = null

      if (type === 'new') {
        const passwordSecure = checkPasswordSecurity(
          this.state.newPassword,
          this.props.passwordStrength,
        )

        if (!passwordSecure) {
          error = this._getSecurityRequirementsErrorCaption(
            <WeakPasswordError
              passwordStrength={this.props.passwordStrength}
            />,
          )
          this.setState({
            securityRequirementsError: error,
          })
        }
      }

      switch (type) {
        case 'server':
          error = this.props.passwordChangeError ? (
            <LoginError
              // @ts-expect-error
              error={this.props.passwordChangeError}
              passwordStrength={this.props.passwordStrength}
            />
          ) : null
          this.setState({
            oldPasswordError: error,
            serverError: error,
          })
          break

        case 'new':
          this.setState({
            newPasswordError: error,
          })

          if (this.state.passwordConfirmation) {
            this.setState({
              passwordConfirmationError: validationMessages,
            })
          }

          break

        case 'confirm':
          error = error || validationMessages
          this.setState({
            passwordConfirmationError: error,
          })
          break

        default:
          break
      }
    }

    this.props.validate(validateCb)
  }
  _onOldPasswordChange = e => {
    const { onInput } = this.props

    if (onInput) {
      onInput('old-password', e.target.value)
    }

    this.setState({
      oldPassword: e.target.value,
    })

    this._resetErrors()
  }
  _onNewPasswordChange = e => {
    const { onInput } = this.props

    if (onInput) {
      onInput('new-password', e.target.value)
    }

    this.setState({
      newPassword: e.target.value,
    })

    this._resetErrors()
  }
  _onPasswordConfirmationChange = e => {
    const { onInput } = this.props

    if (onInput) {
      onInput('re-enter-new-password', e.target.value)
    }

    this.setState({
      passwordConfirmation: e.target.value,
    })

    this._resetErrors()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.passwordChangeError != this.props.passwordChangeError) {
      this._checkErrors('server')
    }
  }

  render() {
    const { newPasswordError, oldPasswordError, passwordConfirmationError } =
      this.state
    const errors = {}
    const weakPasswordContent = ['extra_strong', 'strong'].includes(
      this.props.passwordStrength,
    ) && (
      <p className="ChangePassword_PasswordRequirementsList">
        Password must meet the following requirements
        <PasswordRequirementsList
          passwordStrength={this.props.passwordStrength}
          omitMinimumChars={false}
        />
      </p>
    )

    if (oldPasswordError) {
      errors['current-password'] = oldPasswordError
    }

    if (newPasswordError) {
      errors['new-password'] = newPasswordError
    }

    if (passwordConfirmationError) {
      errors['re-enter-new-password'] = passwordConfirmationError
    }

    if (this.props.passwordChangeError) {
      errors['new-password'] = BREACH_ERROR
    }

    if (this.state.securityRequirementsError) {
      errors['weak-password'] = true
    }

    const commonProps = {
      passwordStrength: this.props.passwordStrength,
      errors,
      passwordConfirmation: this.state.passwordConfirmation,
      passwordChangeInProgress: this.props.passwordChangeInProgress,
      onSubmit: this._changePassword,
      onPasswordConfirmationChange: this._onPasswordConfirmationChange,
    }
    let content = null

    switch (this.props.type) {
      case 'settings':
        content = (
          <ChangePasswordSettingsForm
            {...commonProps}
            oldPassword={this.state.oldPassword}
            newPassword={this.state.newPassword}
            onOldPasswordChange={this._onOldPasswordChange}
            onNewPasswordChange={this._onNewPasswordChange}
          />
        )
        break

      case 'login':
        content = (
          <ChangePasswordLoginForm
            {...commonProps}
            password={this.state.newPassword}
            onPasswordChange={this._onNewPasswordChange}
          />
        )
        break

      case 'welcome':
        content = (
          <ChangePasswordWelcomeForm
            {...commonProps}
            password={this.state.newPassword}
            onPasswordChange={this._onNewPasswordChange}
          />
        )
        break

      case 'invite':
        content = (
          <ChangePasswordInvitesForm
            {...commonProps}
            password={this.state.newPassword}
            onPasswordChange={this._onNewPasswordChange}
          />
        )
        break

      default:
        break
    }

    return (
      <>
        {weakPasswordContent}
        {content}
      </>
    )
  }
}

export default validation(strategy)(
  ChangePassword,
) as React.ComponentType<Props>
